import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Upload, Zap, Wand2, Layers, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, fileUrl, track } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";
import GenCard, { useGenerationPoll } from "../components/GenCard";

const SHOT_TYPES = ["extreme close-up", "close-up", "medium", "medium wide", "wide", "extreme wide", "over-the-shoulder", "POV", "aerial", "macro"];
const CAMERAS = ["static", "push in", "pull out", "pan left", "pan right", "tilt", "orbit", "tracking", "handheld", "crane", "dolly", "drone"];
const LENSES = ["24mm", "35mm", "50mm", "85mm", "100mm macro", "anamorphic"];
const MOTIONS = ["subtle", "natural", "cinematic", "dynamic", "extreme"];
const LIGHTS = ["golden hour", "night", "neon", "studio", "dramatic", "overcast", "candlelight", "backlit"];
const MOODS = ["neutral", "tense", "romantic", "epic", "melancholic", "playful", "eerie", "luxurious"];
const ENVS = ["", "rain", "snow", "fog", "smoke", "dust", "wind", "fire", "particles"];
const STYLES = ["cinematic", "photorealistic", "hollywood", "anime", "cartoon", "3d animation", "documentary", "fashion", "sci-fi", "fantasy", "horror", "vintage"];
const QUALITIES = [["preview", "Preview"], ["standard", "Standard"], ["production", "Production"], ["cinema", "Cinema"]];
const RATIOS = ["16:9", "9:16", "1:1", "4:5", "21:9"];
const DURATIONS = [5, 8, 10, 15, 30];
const RES = ["480p", "720p", "1080p", "4k"];

const sel = "w-full rounded-lg border border-[#212230] bg-[#101118] px-3 py-2.5 text-[12.5px] outline-none focus:border-[#ff4b2b]/60";

function Field({ label, children }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export default function Create() {
  const { user, refresh } = useAuth();
  const { locale } = useI18n();
  const [kind, setKind] = useState("video");
  const [f, setF] = useState({
    prompt: "", duration: 5, aspect_ratio: "16:9", resolution: "720p", quality: "standard",
    variations: 1, generate_audio: true, routing: "auto", model_id: "", shot_type: "medium", camera: "static",
    lens: "35mm", movement: "cinematic", lighting: "dramatic", mood: "neutral",
    environment: "", style: "cinematic", negative_prompt: "",
    character_ids: [], location_id: "", brand_kit_id: "", reference_asset_id: "",
  });
  const [models, setModels] = useState([]);
  const [ents, setEnts] = useState(null);
  const [chars, setChars] = useState([]);
  const [locs, setLocs] = useState([]);
  const [kits, setKits] = useState([]);
  const [est, setEst] = useState(null);
  const [ids, setIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [shotList, setShotList] = useState([]);

  useEffect(() => {
    api.get(`/models?kind=${kind}`).then(({ data }) => {
      setModels(data.models);
      setEnts(data.entitlements || null);
    }).catch(() => {});
  }, [kind]);
  useEffect(() => {
    api.get("/characters").then(({ data }) => setChars(data)).catch(() => {});
    api.get("/locations").then(({ data }) => setLocs(data)).catch(() => {});
    api.get("/brand-kits").then(({ data }) => setKits(data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.post("/generations/estimate", {
      kind, duration: Number(f.duration), resolution: f.resolution, quality: f.quality,
      variations: Number(f.variations), routing: f.routing, model_id: f.model_id || null,
    }).then(({ data }) => setEst(data)).catch(() => {});
  }, [kind, f.duration, f.resolution, f.quality, f.variations, f.routing, f.model_id]);

  const onDone = useCallback(() => refresh(), [refresh]);
  const gens = useGenerationPoll(ids, onDone);

  const activeModel = models.find((m) => m.id === f.model_id) || null;
  const resOrder = ["480p", "720p", "1080p", "4k"];
  const allowedRes = ents
    ? RES.slice(0, resOrder.indexOf(ents.max_resolution) + 1)
    : RES;
  const allowedQualities = ents
    ? QUALITIES.slice(0, QUALITIES.findIndex(([v]) => v === ents.max_quality) + 1)
    : QUALITIES;
  const allowedVariations = [1, 2, 4, 8].filter((v) => !ents || v <= ents.max_variations);
  const engineCap = activeModel?.max_duration || 30;
  const planCap = ents?.max_shot_duration || 30;
  const durationOptions = activeModel?.duration_values?.length
    ? activeModel.duration_values.map(Number)
    : DURATIONS;
  const allowedDurations = durationOptions.filter((d) => d <= Math.min(engineCap, planCap));
  // Locked options stay visible with a padlock: hiding them made the plan feel broken
  // rather than upgradable. `plan` locks sell an upgrade, `engine` locks just need
  // another engine.
  const lockReason = (kind === "video"
    ? durationOptions.map((d) => [d, d > planCap ? "plan" : d > engineCap ? "engine" : null])
    : []);
  const resLocked = (r) => !!ents && resOrder.indexOf(r) > resOrder.indexOf(ents.max_resolution);
  const qualityLocked = (v) =>
    !!ents && QUALITIES.findIndex(([q]) => q === v) > QUALITIES.findIndex(([q]) => q === ents.max_quality);
  const variationLocked = (v) => !!ents && v > ents.max_variations;
  const hasPlanLock = !!ents && (
    lockReason.some(([, why]) => why === "plan") || RES.some(resLocked)
    || QUALITIES.some(([v]) => qualityLocked(v)) || [1, 2, 4, 8].some(variationLocked)
  );
  const lockLabel = locale === "fr" ? "offre supérieure" : "upgrade";

  useEffect(() => {
    if (kind !== "video") return;
    if (allowedDurations.length && !allowedDurations.includes(Number(f.duration))) {
      const fit = allowedDurations.filter((d) => d <= Number(f.duration));
      setF((prev) => ({ ...prev, duration: fit.length ? fit[fit.length - 1] : allowedDurations[0] }));
    }
    if (allowedRes.length && !allowedRes.includes(f.resolution))
      setF((prev) => ({ ...prev, resolution: allowedRes[allowedRes.length - 1] }));
    if (allowedQualities.length && !allowedQualities.some(([v]) => v === f.quality))
      setF((prev) => ({ ...prev, quality: allowedQualities[allowedQualities.length - 1][0] }));
    if (allowedVariations.length && !allowedVariations.includes(Number(f.variations)))
      setF((prev) => ({ ...prev, variations: allowedVariations[allowedVariations.length - 1] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, ents, f.model_id]);

  const fr = locale === "fr";
  const deficit = est ? Math.max(0, est.credits - Number(user.credits || 0)) : 0;
  const cheapest = models
    .filter((m) => !m.locked && m.kind === kind && m.id !== est?.model_id &&
      (kind === "video" ? m.credits_per_second > 0 : m.credits_flat > 0))
    .sort((a, b) => (kind === "video"
      ? a.credits_per_second - b.credits_per_second
      : a.credits_flat - b.credits_flat))[0];
  const currentRate = kind === "video"
    ? models.find((m) => m.id === est?.model_id)?.credits_per_second || 0
    : models.find((m) => m.id === est?.model_id)?.credits_flat || 0;
  const cheapestRate = cheapest
    ? (kind === "video" ? cheapest.credits_per_second : cheapest.credits_flat) : 0;
  const savingTips = (() => {
    if (!est || est.sufficient) return [];
    const out = [];
    const minRes = allowedRes[0];
    if (kind === "video" && f.resolution !== minRes)
      out.push(fr ? `Passer la résolution en ${minRes} (jusqu'à −40 %)`
                  : `Drop the resolution to ${minRes} (up to −40%)`);
    if (f.quality !== "preview" && allowedQualities.some(([v]) => v === "preview"))
      out.push(fr ? "Choisir la qualité Preview pour itérer (−65 %)"
                  : "Switch to Preview quality to iterate (−65%)");
    if (kind === "video" && allowedDurations.length && Number(f.duration) > allowedDurations[0])
      out.push(fr ? `Réduire la durée du plan à ${allowedDurations[0]} s`
                  : `Shorten the shot to ${allowedDurations[0]}s`);
    if (Number(f.variations) > 1)
      out.push(fr ? "Générer une seule variante" : "Generate a single variation");
    if (cheapest && currentRate && cheapestRate && cheapestRate < currentRate)
      out.push(fr ? `Utiliser le moteur ${cheapest.label} (plus économique)`
                  : `Use the ${cheapest.label} engine (cheaper)`);
    if (kind === "video")
      out.push(fr ? "Ou valider le cadre en image (10 crédits) avant d'animer"
                  : "Or approve the frame as an image (10 credits) before animating");
    return out;
  })();

  const fullPrompt = () => {    const bits = [f.prompt];
    if (kind === "video")
      bits.push(
        `Shot: ${f.shot_type}. Camera: ${f.camera}, ${f.lens}. Motion: ${f.movement}. ` +
        `Lighting: ${f.lighting}. Mood: ${f.mood}. Style: ${f.style}.` +
        (f.environment ? ` Environment effect: ${f.environment}.` : "")
      );
    else bits.push(`Style: ${f.style}. Lighting: ${f.lighting}. Mood: ${f.mood}.`);
    return bits.join(" ");
  };

  const enhance = async () => {
    if (!f.prompt.trim()) return;
    setEnhancing(true);
    try {
      const { data } = await api.post("/ai/enhance-prompt", {
        idea: f.prompt, style: f.style, language: locale,
      });
      setF((s) => ({ ...s, prompt: data.full_prompt || s.prompt }));
      toast.success(locale === "fr" ? "Prompt enrichi" : "Prompt enhanced");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setEnhancing(false);
    }
  };

  const buildShots = async () => {
    if (!f.prompt.trim()) return;
    try {
      const { data } = await api.post("/ai/shot-list", {
        description: f.prompt, count: 5, style: f.style,
      });
      setShotList(data.shots || []);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/assets/upload", fd);
      setF((s) => ({ ...s, reference_asset_id: data.id }));
      toast.success(locale === "fr" ? "Référence ajoutée" : "Reference added");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const generate = async (overridePrompt) => {
    setBusy(true);
    try {
      const payload = {
        kind,
        prompt: overridePrompt || fullPrompt(),
        title: f.prompt.slice(0, 60),
        duration: Number(f.duration),
        resolution: f.resolution,
        aspect_ratio: f.aspect_ratio,
        quality: f.quality,
        variations: Number(f.variations),
        routing: f.routing,
        model_id: f.model_id || null,
        reference_asset_id: f.reference_asset_id || null,
        character_ids: f.character_ids,
        location_id: f.location_id || null,
        brand_kit_id: f.brand_kit_id || null,
        negative_prompt: f.negative_prompt,
        generate_audio: f.generate_audio !== false,
      };
      const batch = kind === "video" && Number(f.variations) > 1;
      if (batch) {
        const { data } = await api.post("/media/batch", {
          count: Number(f.variations),
          payload: { ...payload, variations: 1 },
        });
        track("generation_request", { kind, quality: f.quality, batch: data.count });
        setIds((s) => [...data.generations.map((g) => g.id), ...s].slice(0, 12));
      } else {
        const { data } = await api.post("/generations", payload);
        track("generation_request", { kind, quality: f.quality });
        setIds((s) => [data.id, ...s].slice(0, 12));
      }
      await refresh();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-0 xl:grid-cols-[minmax(0,1fr)_380px]" data-testid="create-page">
      <div className="p-6 lg:p-10">
        <p className="overline">{locale === "fr" ? "Shot Director" : "Shot Director"}</p>
        <h1 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">
          {locale === "fr" ? "Que voulez-vous créer ?" : "What do you want to create?"}
        </h1>

        <div className="mt-8 flex gap-2">
          {[["video", locale === "fr" ? "Vidéo" : "Video"], ["image", "Image"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              data-testid={`create-kind-${k}`}
              className={`rounded-full px-5 py-2 text-[12.5px] font-semibold transition-colors duration-200 ${
                kind === k ? "bg-white text-black" : "border border-[#2a2c3c] text-[#9194a6] hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-[#212230] bg-[#0b0b11] p-6">
          <textarea
            rows={4}
            value={f.prompt}
            onChange={(e) => setF({ ...f, prompt: e.target.value })}
            data-testid="create-prompt"
            placeholder={
              locale === "fr"
                ? "Une femme élégante marche dans une rue parisienne sous la pluie, la nuit…"
                : "An elegant woman walking a rainy Paris street at night…"
            }
            className="w-full resize-none bg-transparent text-[15px] leading-relaxed outline-none placeholder:text-[#8288a0]"
          />
          <div className="mt-4 flex flex-wrap gap-2.5">
            <button
              onClick={enhance}
              disabled={enhancing}
              data-testid="create-enhance"
              className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11.5px] font-semibold disabled:opacity-50"
            >
              <Sparkles className="h-3 w-3" /> {enhancing ? "…" : locale === "fr" ? "Enrichir le prompt" : "Enhance my prompt"}
            </button>
            <button
              onClick={buildShots}
              data-testid="create-shotlist"
              className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11.5px] font-semibold"
            >
              <Layers className="h-3 w-3" /> {locale === "fr" ? "Créer 5 plans" : "Create 5 shots"}
            </button>
            <label className="btn-ghost inline-flex cursor-pointer items-center gap-1.5 rounded-full px-4 py-2 text-[11.5px] font-semibold">
              <Upload className="h-3 w-3" />
              {f.reference_asset_id ? (locale === "fr" ? "Référence OK" : "Reference set") : locale === "fr" ? "Référence" : "Reference"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="create-upload"
                onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
              />
            </label>
          </div>
          {f.reference_asset_id && (
            <img src={fileUrl(f.reference_asset_id)} alt="" className="mt-4 h-24 w-24 rounded-lg object-cover" />
          )}
        </div>

        {shotList.length > 0 && (
          <div className="mt-6 rounded-2xl border border-[#212230] bg-[#0b0b11] p-6" data-testid="shot-list">
            <p className="overline">{locale === "fr" ? "Liste de plans proposée" : "Proposed shot list"}</p>
            <div className="mt-4 space-y-3">
              {shotList.map((s, i) => (
                <div key={i} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#1a1b26] bg-[#0e0e15] p-4">
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium">
                      <span className="mono mr-2 text-[#ff4b2b]">SHOT {String(i + 1).padStart(2, "0")}</span>
                      {s.title}
                    </p>
                    <p className="mono mt-1 text-[10.5px] uppercase text-[#8288a0]">
                      {s.shot_type} · {s.camera} · {s.lens} · {s.duration}s
                    </p>
                  </div>
                  <button
                    onClick={() => generate(s.prompt)}
                    data-testid={`shotlist-generate-${i}`}
                    className="btn-ghost shrink-0 rounded-full px-4 py-1.5 text-[11px] font-semibold"
                  >
                    {locale === "fr" ? "Générer" : "Generate"}
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => shotList.forEach((s) => generate(s.prompt))}
              data-testid="shotlist-generate-all"
              className="btn-amber mt-5 rounded-full px-5 py-2.5 text-[12px] font-semibold"
            >
              {locale === "fr" ? "Générer tous les plans" : "Generate all shots"}
            </button>
          </div>
        )}

        {ids.length === 0 && (
          <div className="lit mt-10 overflow-hidden rounded-2xl border border-[#212230] bg-[#0b0b11] p-8 lg:p-10" data-testid="create-empty-canvas">
            <p className="overline">{locale === "fr" ? "Comment ça marche" : "How it works"}</p>
            <h2 className="mt-4 max-w-[520px] font-display text-2xl font-medium">
              {locale === "fr"
                ? "Un plan à la fois. Un film à la fin."
                : "One shot at a time. A film at the end."}
            </h2>
            <div className="mt-9 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {(locale === "fr"
                ? [["01", "Décrivez", "Écrivez votre idée ou choisissez un template. Enhance transforme la phrase en prompt cinématographique."],
                   ["02", "Verrouillez", "Ajoutez un personnage, un décor et un brand kit : ils resteront identiques d'un plan à l'autre."],
                   ["03", "Générez", "Preview pour chercher, Cinema pour livrer. Le coût s'affiche avant de lancer."],
                   ["04", "Assemblez", "Envoyez le plan dans un projet, montez la timeline et exportez le film final."]]
                : [["01", "Describe", "Write your idea or pick a template. Enhance turns the sentence into a cinematic prompt."],
                   ["02", "Lock", "Add a character, a location and a brand kit — they stay identical from shot to shot."],
                   ["03", "Generate", "Preview to explore, Cinema to deliver. The cost is shown before you commit."],
                   ["04", "Assemble", "Push the shot into a project, cut the timeline and export the final film."]]
              ).map(([n, title, body]) => (
                <div key={n} className="rounded-xl border border-[#1a1b26] bg-[#0e0e15] p-5">
                  <span className="mono text-[10px] tracking-[0.2em] text-[#ff4b2b]">{n}</span>
                  <p className="mt-3 text-[13px] font-medium">{title}</p>
                  <p className="mt-2 text-[12px] leading-relaxed text-[#8288a0]">{body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {ids.length > 0 && (
          <div className="mt-10">
            <p className="overline">{locale === "fr" ? "Générations" : "Generations"}</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {ids.map((id) => (
                <GenCard
                  key={id}
                  gen={gens[id]}
                  onReuse={(g) => {
                    const p = g.params || {};
                    setF((prev) => ({
                      ...prev,
                      prompt: p.raw_prompt || p.prompt || prev.prompt,
                      negative_prompt: p.negative_prompt ?? prev.negative_prompt,
                      duration: p.duration ?? prev.duration,
                      resolution: p.resolution ?? prev.resolution,
                      aspect_ratio: p.aspect_ratio ?? prev.aspect_ratio,
                      quality: p.quality ?? prev.quality,
                      model_id: g.model_id ?? prev.model_id,
                      routing: p.routing ?? prev.routing,
                    }));
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    toast.success(locale === "fr" ? "Réglages réappliqués" : "Setup reapplied");
                  }}
                  onRetry={async (gid) => {
                    await api.post(`/generations/${gid}/retry`).catch((e) => toast.error(apiErr(e)));
                    setIds((s) => [...s]);
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* RIGHT SETTINGS PANEL */}
      <aside className="border-t border-[#212230] bg-[#08080d] p-6 xl:border-l xl:border-t-0 xl:p-7">
        <p className="overline">{locale === "fr" ? "Réglages" : "Settings"}</p>

        <div className="mt-5 space-y-4">
          <Field label={locale === "fr" ? "Style visuel" : "Visual style"}>
            <select className={sel} value={f.style} onChange={(e) => setF({ ...f, style: e.target.value })} data-testid="create-style">
              {STYLES.map((s) => <option key={s} value={s} children={String(s)} />)}
            </select>
          </Field>

          {kind === "video" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label={locale === "fr" ? "Plan" : "Shot"}>
                  <select className={sel} value={f.shot_type} onChange={(e) => setF({ ...f, shot_type: e.target.value })} data-testid="create-shottype">
                    {SHOT_TYPES.map((s) => <option key={s} value={s} children={String(s)} />)}
                  </select>
                </Field>
                <Field label="Camera">
                  <select className={sel} value={f.camera} onChange={(e) => setF({ ...f, camera: e.target.value })} data-testid="create-camera">
                    {CAMERAS.map((s) => <option key={s} value={s} children={String(s)} />)}
                  </select>
                </Field>
                <Field label="Lens">
                  <select className={sel} value={f.lens} onChange={(e) => setF({ ...f, lens: e.target.value })}>
                    {LENSES.map((s) => <option key={s} value={s} children={String(s)} />)}
                  </select>
                </Field>
                <Field label="Motion">
                  <select className={sel} value={f.movement} onChange={(e) => setF({ ...f, movement: e.target.value })}>
                    {MOTIONS.map((s) => <option key={s} value={s} children={String(s)} />)}
                  </select>
                </Field>
                <Field label="Lighting">
                  <select className={sel} value={f.lighting} onChange={(e) => setF({ ...f, lighting: e.target.value })}>
                    {LIGHTS.map((s) => <option key={s} value={s} children={String(s)} />)}
                  </select>
                </Field>
                <Field label="Mood">
                  <select className={sel} value={f.mood} onChange={(e) => setF({ ...f, mood: e.target.value })}>
                    {MOODS.map((s) => <option key={s} value={s} children={String(s)} />)}
                  </select>
                </Field>
                <Field label={locale === "fr" ? "Effet" : "Environment"}>
                  <select className={sel} value={f.environment} onChange={(e) => setF({ ...f, environment: e.target.value })}>
                    {ENVS.map((s) => <option key={s} value={s} children={s || "none"} />)}
                  </select>
                </Field>
                <Field label={locale === "fr" ? "Durée du plan" : "Shot length"}>
                  <select className={sel} value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} data-testid="create-duration">
                    {lockReason.map(([s, why]) => (
                      <option key={s} value={s} disabled={!!why}>
                        {why === "plan" ? `${s}s 🔒 ${lockLabel}`
                          : why === "engine" ? `${s}s — ${locale === "fr" ? "autre moteur" : "other engine"}`
                          : `${s}s`}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <p className="mono mt-3 text-[10.5px] leading-relaxed text-[#8288a0]" data-testid="duration-help">
                {locale === "fr"
                  ? `Un plan = une génération vidéo. ${activeModel ? `${activeModel.label} va jusqu'à ${activeModel.max_duration}s` : "Chaque moteur a sa durée max"}${ents ? ` · votre offre ${ents.plan_name} autorise ${ents.max_shot_duration}s par plan et ${Math.round(ents.max_film_duration / 60)} min de film` : ""}. Pour un film plus long, assemblez plusieurs plans dans un projet.`
                  : `One shot = one video generation. ${activeModel ? `${activeModel.label} goes up to ${activeModel.max_duration}s` : "Each engine has its own cap"}${ents ? ` · your ${ents.plan_name} plan allows ${ents.max_shot_duration}s per shot and ${Math.round(ents.max_film_duration / 60)} min of film` : ""}. For a longer film, assemble several shots in a project.`}
              </p>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Ratio">
              <select className={sel} value={f.aspect_ratio} onChange={(e) => setF({ ...f, aspect_ratio: e.target.value })} data-testid="create-ratio">
                {RATIOS.map((s) => <option key={s} value={s} children={String(s)} />)}
              </select>
            </Field>
            <Field label={locale === "fr" ? "Résolution" : "Resolution"}>
              <select className={sel} value={f.resolution} onChange={(e) => setF({ ...f, resolution: e.target.value })} data-testid="create-resolution">
                {RES.map((s) => (
                  <option key={s} value={s} disabled={resLocked(s)}>
                    {resLocked(s) ? `${s} 🔒 ${lockLabel}` : s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={locale === "fr" ? "Qualité" : "Quality"}>
              <select className={sel} value={f.quality} onChange={(e) => setF({ ...f, quality: e.target.value })} data-testid="create-quality">
                {QUALITIES.map(([v, l]) => (
                  <option key={v} value={v} disabled={qualityLocked(v)}>
                    {qualityLocked(v) ? `${l} 🔒 ${lockLabel}` : l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={locale === "fr" ? "Variantes" : "Variations"}>
              <select className={sel} value={f.variations} onChange={(e) => setF({ ...f, variations: e.target.value })} data-testid="create-variations">
                {[1, 2, 4, 8].map((s) => (
                  <option key={s} value={s} disabled={variationLocked(s)}>
                    {variationLocked(s) ? `${s} 🔒 ${lockLabel}` : s}
                  </option>
                ))}
              </select>
            </Field>
            {kind === "video" && (
              <Field label={locale === "fr" ? "Audio natif" : "Native audio"}>
                <button
                  type="button"
                  onClick={() => setF({ ...f, generate_audio: f.generate_audio === false })}
                  data-testid="create-native-audio"
                  className={`w-full rounded-lg border px-3 py-2.5 text-left text-[12.5px] transition-colors ${
                    f.generate_audio === false
                      ? "border-[#212230] text-[#8288a0]"
                      : "border-[#00d5ff]/50 bg-[#00d5ff]/[0.08] text-[#7de8ff]"
                  }`}
                >
                  {f.generate_audio === false
                    ? (locale === "fr" ? "Désactivé" : "Off")
                    : (locale === "fr" ? "Activé · SFX + dialogue" : "On · SFX + dialogue")}
                </button>
              </Field>
            )}
            <Field label="Router">
              <select className={sel} value={f.routing} onChange={(e) => setF({ ...f, routing: e.target.value })} data-testid="create-routing">
                {["auto", "fast", "quality", "cinema", "cost_saver"].map((s) => <option key={s} value={s} children={String(s)} />)}
              </select>
            </Field>
            <Field label="Model">
              <select className={sel} value={f.model_id} onChange={(e) => setF({ ...f, model_id: e.target.value })} data-testid="create-model">
                <option value="">Automatic</option>
                {models.map((m) => <option key={m.id} value={m.id} disabled={m.locked} children={m.locked ? `${m.label} — ${locale === "fr" ? "offre supérieure" : "upgrade"}` : m.degraded ? `${m.label} — ${locale === "fr" ? "instable" : "unstable"}` : m.label} />)}
              </select>
            </Field>
          </div>

          {hasPlanLock && (
            <Link
              to={`/${locale}/pricing`}
              data-testid="plan-locks-upsell"
              className="mt-4 flex items-start gap-2.5 rounded-lg border border-[#ffc76b]/30 bg-[#ffc76b]/[0.07] p-3 text-[11.5px] leading-relaxed text-[#ffc76b] transition-colors duration-200 hover:border-[#ffc76b]/60"
            >
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {locale === "fr"
                  ? `Offre ${ents.plan_name} : ${ents.max_shot_duration} s par plan, ${ents.max_resolution} maximum, ${ents.max_variations} variante${ents.max_variations > 1 ? "s" : ""}. Les options 🔒 (plans plus longs, 1080p et 4K, qualité Cinema, variantes multiples, moteurs premium) se débloquent dès l'offre Starter — voir les offres et les packs de crédits.`
                  : `${ents.plan_name} plan: ${ents.max_shot_duration}s per shot, ${ents.max_resolution} maximum, ${ents.max_variations} variation${ents.max_variations > 1 ? "s" : ""}. The 🔒 options (longer shots, 1080p and 4K, Cinema quality, multiple variations, premium engines) unlock from the Starter plan — see plans and credit packs.`}
              </span>
            </Link>
          )}

          {chars.length > 0 && (
            <Field label={locale === "fr" ? "Personnage (lock)" : "Character lock"}>
              <select
                className={sel}
                value={f.character_ids[0] || ""}
                onChange={(e) => setF({ ...f, character_ids: e.target.value ? [e.target.value] : [] })}
                data-testid="create-character"
              >
                <option value="">—</option>
                {chars.map((c) => <option key={c.id} value={c.id} children={`${c.code} · ${c.name}`} />)}
              </select>
            </Field>
          )}
          {locs.length > 0 && (
            <Field label={locale === "fr" ? "Décor (lock)" : "Location lock"}>
              <select className={sel} value={f.location_id} onChange={(e) => setF({ ...f, location_id: e.target.value })} data-testid="create-location">
                <option value="">—</option>
                {locs.map((c) => <option key={c.id} value={c.id} children={`${c.code} · ${c.name}`} />)}
              </select>
            </Field>
          )}
          {kits.length > 0 && (
            <Field label="Brand Kit">
              <select className={sel} value={f.brand_kit_id} onChange={(e) => setF({ ...f, brand_kit_id: e.target.value })}>
                <option value="">—</option>
                {kits.map((c) => <option key={c.id} value={c.id} children={c.name} />)}
              </select>
            </Field>
          )}

          <Field label={locale === "fr" ? "À éviter" : "Negative instructions"}>
            <input
              className={sel}
              value={f.negative_prompt}
              onChange={(e) => setF({ ...f, negative_prompt: e.target.value })}
              placeholder="blurry, text, watermark"
            />
          </Field>
        </div>

        <div className="mt-7 rounded-xl border border-[#212230] bg-[#0e0e15] p-5">
          <div className="flex items-center justify-between">
            <p className="mono text-[10px] uppercase tracking-[0.16em] text-[#8288a0]">
              {locale === "fr" ? "Coût estimé" : "Estimated cost"}
            </p>
            <p className="mono text-[10px] text-[#9194a6]">{est?.model_label}</p>
          </div>
          <p className="mt-2 flex items-center gap-2 font-display text-3xl font-semibold text-[#ff4b2b]" data-testid="estimated-cost">
            <Zap className="h-5 w-5" />
            {est?.credits ?? "—"}
            <span className="text-sm font-normal text-[#9194a6]">credits</span>
          </p>
          <p className="mono mt-2 text-[10.5px] text-[#8288a0]">
            {locale === "fr" ? "Disponible" : "Available"} · {Number(user.credits).toLocaleString()}
          </p>
          {est?.notices?.length > 0 && (
            <ul className="mt-3 space-y-1" data-testid="plan-notices">
              {est.notices.map((n) => (
                <li key={n} className="text-[11px] leading-relaxed text-[#ffc76b]">• {n}</li>
              ))}
            </ul>
          )}
          {est && !est.sufficient && (
            <div className="mt-3 rounded-lg border border-[#ff8a8a]/30 bg-[#ff4b2b]/[0.06] p-3" data-testid="insufficient-credits">
              <p className="text-[12px] font-semibold text-[#ff8a8a]">
                {locale === "fr"
                  ? `Il vous manque ${deficit.toLocaleString()} crédit${deficit > 1 ? "s" : ""}.`
                  : `You are ${deficit.toLocaleString()} credit${deficit > 1 ? "s" : ""} short.`}
              </p>
              {savingTips.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="credit-suggestions">
                  {savingTips.map((s) => (
                    <li key={s} className="text-[11.5px] leading-relaxed text-[#c0c3d0]">• {s}</li>
                  ))}
                </ul>
              )}
              <Link
                to="/studio/credits"
                data-testid="buy-credits-link"
                className="btn-ghost mt-3 inline-flex rounded-full px-4 py-1.5 text-[11px] font-semibold"
              >
                {locale === "fr" ? "Ajouter des crédits" : "Add credits"}
              </Link>
            </div>
          )}
        </div>

        <button
          onClick={() => generate()}
          disabled={busy || !f.prompt.trim() || (est && !est.sufficient)}
          data-testid="generate-btn"
          className="btn-amber mt-5 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold disabled:opacity-40"
        >
          <Wand2 className="h-4 w-4" />
          {busy ? "…" : locale === "fr" ? "Générer" : "Generate"}
        </button>
      </aside>
    </div>
  );
}
